import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * Purpose: Maintains a reactive map of username → latest avatar attachment ID with timestamp tracking.
 *
 * How it works:
 * 1. Every time we learn about a user's avatar (from incoming messages,
 *    websocket user events, API fetches, or self-updates), we call setUserAvatar.
 * 2. Each avatar entry includes the timestamp when it was set to prevent race conditions.
 * 3. Updates are only applied if they're newer than the existing entry (or from priority sources).
 * 4. Display components subscribe via useUserAvatar() hook and always
 *    render the most recent avatar for a given sender, regardless of which
 *    message they are looking at.
 *
 * Key features:
 * - Single source of truth for "latest avatar per user"
 * - Race condition prevention via timestamp comparison
 * - Reactive: zustand subscriptions cause automatic re-renders
 * - Agnostic to update source (message, websocket, self-change)
 * - Stores attachment IDs (not base64 blobs)
 *
 * Race condition handling:
 * - Message-derived avatars: Use message timestamp (UUID7 extraction)
 * - User profile events: Use current time with priority=true (override immediately)
 * - Priority flag: Bypasses timestamp comparison for immediate acceptance
 * - All timestamps are real dates - newer timestamps always win eventually
 */

type AvatarEntry = {
  attachmentId: string;
  /** ISO 8601 timestamp when this avatar was set */
  timestamp: string;
  /** Set when this entry was written by a priority source (server sync).
   *  Non-priority updates within SYNC_GRACE_PERIOD_MS are rejected to
   *  prevent replayed FETCH_EVENTS from overwriting ground-truth data. */
  syncedAt?: string;
};

type UserAvatarMapState = {
  avatarMap: Record<string, AvatarEntry>;
};

type Actions = {
  /**
   * Update the latest avatar attachment ID for a single user.
   * Only updates if the new timestamp is newer than existing (or priority=true).
   *
   * @param username - Username to update
   * @param attachmentId - Avatar attachment ID
   * @param timestamp - ISO timestamp when avatar was set (use current time for profile events)
   * @param priority - If true, accept immediately regardless of existing timestamp (for user profile events)
   */
  setUserAvatar: (
    username: string,
    attachmentId: string,
    timestamp: string,
    priority?: boolean,
  ) => void;
  /**
   * Batch-update avatars from a list of messages (sender + sender_avatar pairs).
   * Only updates entries that are newer than existing values.
   *
   * @param entries - Array of avatar entries with timestamps
   */
  setUserAvatarBatch: (
    entries: Array<{
      username: string;
      attachmentId: string;
      timestamp: string;
    }>,
  ) => void;
  /** Tombstone a user's avatar (stores empty attachmentId so useUserAvatar
   *  returns "" instead of falling back to stale per-message data).
   *  @param priority - If true, bypass grace period and mark with syncedAt (for server sync / self-deletes) */
  removeUserAvatar: (username: string, priority?: boolean) => void;
  /** Reset the entire map (e.g. on logout) */
  resetUserAvatarMap: () => void;
};

const initialState: UserAvatarMapState = {
  avatarMap: {},
};

// Prune to this size on rehydration to keep localStorage bounded
const MAX_AVATAR_MAP_SIZE = 1000;

// After a priority (server-sync) write, reject non-priority updates for this
// duration. Prevents replayed FETCH_EVENTS from overwriting the sync baseline
// with stale avatar data. Real-time events after this window update normally.
const SYNC_GRACE_PERIOD_MS = 30_000;

export const useUserAvatarMapState = create<UserAvatarMapState & Actions>()(
  persist(
    (set) => ({
      ...initialState,

      setUserAvatar: (username, attachmentId, timestamp, priority = false) => {
        if (!username || !attachmentId || !timestamp) {
          return;
        }
        set((state) => {
          const existing = state.avatarMap[username];

          // Skip update if the value is already the same (avoid unnecessary re-renders)
          if (
            existing?.attachmentId === attachmentId &&
            existing?.timestamp === timestamp
          ) {
            return state;
          }

          // If no existing entry, add it
          if (!existing) {
            return {
              avatarMap: {
                ...state.avatarMap,
                [username]: {
                  attachmentId,
                  timestamp,
                  ...(priority && { syncedAt: timestamp }),
                },
              },
            };
          }

          // If priority is set, always accept the update (for server sync / self-updates)
          if (priority) {
            return {
              avatarMap: {
                ...state.avatarMap,
                [username]: { attachmentId, timestamp, syncedAt: timestamp },
              },
            };
          }

          // Protect synced entries from replayed FETCH_EVENTS during reconnection.
          // Without this, replayed events stamped with new Date() would compare as
          // newer than the sync timestamp and overwrite ground-truth data.
          if (existing.syncedAt) {
            const elapsed = Date.now() - new Date(existing.syncedAt).getTime();
            if (elapsed < SYNC_GRACE_PERIOD_MS) {
              return state;
            }
          }

          // Compare timestamps (ISO 8601 strings — lexicographic order is correct)
          if (timestamp > existing.timestamp) {
            return {
              avatarMap: {
                ...state.avatarMap,
                [username]: { attachmentId, timestamp },
              },
            };
          }

          // New timestamp is older, don't update
          return state;
        });
      },

      setUserAvatarBatch: (entries) => {
        if (!entries.length) {
          return;
        }
        set((state) => {
          let changed = false;
          const newMap = { ...state.avatarMap };

          for (const { username, attachmentId, timestamp } of entries) {
            if (!username || !attachmentId || !timestamp) {
              continue;
            }

            const existing = newMap[username];

            // If no existing entry, add it
            if (!existing) {
              newMap[username] = { attachmentId, timestamp };
              changed = true;
              continue;
            }

            // Skip if value is already the same
            if (
              existing.attachmentId === attachmentId &&
              existing.timestamp === timestamp
            ) {
              continue;
            }

            // Protect synced entries from non-priority batch updates (e.g.
            // slow member API responses arriving after a websocket update).
            if (existing.syncedAt) {
              const elapsed =
                Date.now() - new Date(existing.syncedAt).getTime();
              if (elapsed < SYNC_GRACE_PERIOD_MS) {
                continue;
              }
            }

            // Compare timestamps (ISO 8601 strings — lexicographic order is correct)
            if (timestamp > existing.timestamp) {
              newMap[username] = { attachmentId, timestamp };
              changed = true;
            }
          }

          return changed ? { avatarMap: newMap } : state;
        });
      },

      removeUserAvatar: (username, priority = false) => {
        if (!username) {
          return;
        }
        set((state) => {
          const existing = state.avatarMap[username];

          // Priority callers (server sync, self-deletes) bypass the grace period.
          // Non-priority callers respect it — a replayed stale event with
          // empty profile_image must not tombstone an avatar that was just
          // seeded by syncChats with priority=true.
          if (!priority && existing?.syncedAt) {
            const elapsed = Date.now() - new Date(existing.syncedAt).getTime();
            if (elapsed < SYNC_GRACE_PERIOD_MS) {
              return state;
            }
          }

          const now = new Date().toISOString();
          return {
            avatarMap: {
              ...state.avatarMap,
              [username]: {
                attachmentId: "",
                timestamp: now,
                // Priority tombstones get syncedAt so replayed stale events
                // with a profile_image cannot resurrect an old avatar.
                ...(priority && { syncedAt: now }),
              },
            },
          };
        });
      },

      resetUserAvatarMap: () => {
        set(initialState);
      },
    }),
    {
      name: "user-avatar-map",
      version: 1,
      partialize: (state) => ({ avatarMap: state.avatarMap }),
      onRehydrateStorage: () => (state) => {
        if (!state) {
          return;
        }
        const keys = Object.keys(state.avatarMap);
        if (keys.length <= MAX_AVATAR_MAP_SIZE) {
          return;
        }
        const kept = Object.fromEntries(
          Object.entries(state.avatarMap)
            .sort(([, a], [, b]) => b.timestamp.localeCompare(a.timestamp))
            .slice(0, MAX_AVATAR_MAP_SIZE),
        );
        useUserAvatarMapState.setState({ avatarMap: kept });
      },
    },
  ),
);
